import { useBuilderReference } from 'utils/hooks/useBuilderReference';

import type { WistiaFormData, WistiaFormReference } from 'components/WistiaForm/config';

type RawData = Record<string, unknown> | null | undefined;

const pickString = (data: RawData, keys: string[]): string | undefined => {
  if (!data) {
    return undefined;
  }

  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
};

/**
 * Resolves a `wistia-forms` reference into a normalized { formId, embedType }.
 *
 * Builder uses the data-model field NAME as the JSON key, so an entry whose
 * fields were created as "Form id" / "Embed type" yields keys with spaces and
 * capitals. We read every reasonable spelling so the embed works regardless of
 * how the model fields were named in the Builder dashboard.
 *
 * The Content API fetch is gated on the SSR-preloaded value to avoid per-visit
 * Builder bandwidth, matching ContainedConversionPanel / Hero Form.
 */
export const useWistiaFormData = (form?: WistiaFormReference) => {
  const hasPreloadedForm = !!form?.value?.data;
  const refData = useBuilderReference<WistiaFormData>(
    hasPreloadedForm ? undefined : form?.model,
    hasPreloadedForm ? undefined : form?.id,
  );
  const data = (hasPreloadedForm ? form?.value?.data : refData) as RawData;

  const formId = pickString(data, ['formId', 'form-id', 'Form id', 'formID', 'FormId']);
  const isStandardForm = pickString(data, ['embedType', 'embed-type', 'Embed type', 'embedtype']) === 'form';

  return { formId, embedType: (isStandardForm ? 'form' : 'live-event') as 'live-event' | 'form' };
};
